import React, { useMemo } from "react";
import { Tag, Tooltip } from "antd";
import { ThunderboltFilled, ArrowRightOutlined } from "@ant-design/icons";
import { format, formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import MarkdownMessage from "../MarkdownMessage";
import { askAiTokens as t } from "./tokens";
import { AskAiMessage } from "./types";
import ThinkingIndicator from "./ThinkingIndicator";
import MessageActions from "./MessageActions";

// Strip any trailing "Sources" / "References" / "Citations" section emitted
// by the LLM inside the response body. The line that introduces the section
// can be a plain label, a bold span, or a heading — and we drop everything
// from that line to the end of the message.
const SOURCES_LINE_RE =
  /^\s*(?:#{1,6}\s+)?(?:\*\*\s*)?(?:sources?|references?|citations?)(?:\s*\*\*)?\s*:?\s*$/i;

function stripSourcesSection(text: string): string {
  if (!text) return text;
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    if (SOURCES_LINE_RE.test(lines[i])) {
      return lines.slice(0, i).join("\n").trimEnd();
    }
  }
  return text;
}

// Inline citation references look like "(Source: foo.txt)" or
// "(Sources: a.csv, b.pdf)" inside the response body. Convert each to a
// markdown link `[Source: foo.txt](url)` when we know a download URL — the
// MarkdownMessage's <a> renderer picks up the "Source: " prefix and renders
// it as a polished inline pill. If the URL is unknown for any item in the
// list, we leave the parenthetical untouched.
const INLINE_SOURCE_RE =
  /\((?:Sources?|Ref|References?|Citations?):\s*([^)]+?)\)/gi;

function applySourceLinks(
  text: string,
  sources?: Array<{ filename: string; url?: string }>
): string {
  if (!text || !sources || sources.length === 0) return text;
  const lookup = new Map<string, string>();
  sources.forEach((s) => {
    if (s.filename && s.url) {
      lookup.set(s.filename.trim().toLowerCase(), s.url);
    }
  });
  if (lookup.size === 0) return text;

  return text.replace(INLINE_SOURCE_RE, (match, list: string) => {
    const names = list
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length === 0) return match;
    const resolved = names.map((name) => ({
      name,
      url: lookup.get(name.toLowerCase()),
    }));
    if (resolved.some((r) => !r.url)) {
      // At least one filename has no URL — leave the whole parens alone
      // so the user still sees the reference but as plain text.
      return match;
    }
    return resolved
      .map((r) => `[Source: ${r.name}](${r.url})`)
      .join(" ");
  });
}

interface Props {
  message: AskAiMessage;
  thinkingLabel: string;
  copied: boolean;
  feedback: "good" | "bad" | undefined;
  isLastAssistant: boolean;
  canRegenerate: boolean;
  onCopy: () => void;
  onFeedback: (value: "good" | "bad") => void;
  onShare: () => void;
  onRegenerate: () => void;
  onDownloadImage?: () => void;
  onSuggestedQuestion?: (text: string) => void;
}

const AssistantMessage: React.FC<Props> = ({
  message,
  thinkingLabel,
  copied,
  feedback,
  isLastAssistant,
  canRegenerate,
  onCopy,
  onFeedback,
  onShare,
  onRegenerate,
  onDownloadImage,
  onSuggestedQuestion,
}) => {
  const ts = message.ts;
  // Two-step text pipeline: first strip the standalone "Sources:" section
  // (we hide that footer), then promote inline "(Source: foo.txt)" refs
  // into markdown links with the matching download URL.
  const cleanText = useMemo(() => {
    const stripped = stripSourcesSection(message.text || "");
    return applySourceLinks(stripped, message.citations?.sources);
  }, [message.text, message.citations?.sources]);
  const hasContent = Boolean(cleanText);
  const chunkCount = message.citations?.chunks?.length ?? 0;
  const urls = message.citations?.urls ?? [];
  // Sources/citations chips are hidden for now — the LLM tends to mention
  // sources inside the response markdown anyway. Flip this back to
  // `chunkCount > 0 || urls.length > 0` to re-enable.
  const showCitations = false && (chunkCount > 0 || urls.length > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="askai-assistant-turn"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: t.space.s2,
        padding: `${t.space.s4}px 0`,
      }}
    >
      <div
        style={{
          fontSize: t.font.size.xs,
          color: t.color.textSubtle,
          display: "flex",
          gap: t.space.s2,
          alignItems: "center",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontWeight: 600,
            color: t.color.accent,
          }}
        >
          {message.pending ? (
            <motion.span
              animate={{ rotate: [0, 12, -8, 0] }}
              transition={{
                duration: 1.6,
                ease: "easeInOut",
                repeat: Infinity,
              }}
              style={{ display: "inline-flex", fontSize: 10 }}
            >
              <ThunderboltFilled />
            </motion.span>
          ) : (
            <ThunderboltFilled style={{ fontSize: 10 }} />
          )}
          IntelliSuite
        </span>
        {!message.pending && (
          <Tooltip title={format(new Date(ts), "PPpp")}>
            <span>
              {formatDistanceToNow(new Date(ts), { addSuffix: true })}
            </span>
          </Tooltip>
        )}
      </div>

      <div
        style={{
          fontSize: t.font.size.message,
          lineHeight: 1.7,
          color: message.error ? t.color.danger : t.color.text,
        }}
      >
        {hasContent ? (
          <MarkdownMessage text={cleanText} />
        ) : message.pending ? (
          <ThinkingIndicator
            label={thinkingLabel}
            toolEvents={message.toolEvents}
          />
        ) : null}
        {hasContent && message.pending && (message.toolEvents?.length ?? 0) > 0 && (
          <div style={{ marginTop: t.space.s2 }}>
            <ThinkingIndicator
              label={thinkingLabel}
              toolEvents={message.toolEvents}
            />
          </div>
        )}
      </div>

      {message.imageUrl && (
        <img
          src={message.imageUrl}
          alt={message.imageName || "AI response"}
          style={{
            maxWidth: "100%",
            maxHeight: 320,
            borderRadius: t.radius.md,
            border: `1px solid ${t.color.border}`,
          }}
        />
      )}

      {showCitations && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: t.space.s1,
            marginTop: t.space.s1,
          }}
        >
          {chunkCount > 0 && (
            <Tag
              style={{
                background: t.color.surfaceMuted,
                border: `1px solid ${t.color.border}`,
                color: t.color.textMuted,
                fontSize: t.font.size.xs,
              }}
            >
              {chunkCount} doc chunk{chunkCount === 1 ? "" : "s"}
            </Tag>
          )}
          {urls.map((u, i) => (
            <Tag
              key={`${u}-${i}`}
              style={{
                background: t.color.accentSoft,
                border: `1px solid #d6e4ff`,
                fontSize: t.font.size.xs,
              }}
            >
              <a
                href={u}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: t.color.accent }}
              >
                [{i + 1}] source
              </a>
            </Tag>
          ))}
        </div>
      )}

      {!message.pending &&
        hasContent &&
        isLastAssistant &&
        onSuggestedQuestion &&
        message.suggestedQuestions &&
        message.suggestedQuestions.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: t.space.s2,
              marginTop: t.space.s3,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: t.color.textMuted,
              }}
            >
              Suggested follow-ups
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {message.suggestedQuestions.map((q, i) => (
                <motion.button
                  key={`${q}-${i}`}
                  type="button"
                  onClick={() => onSuggestedQuestion(q)}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.28,
                    delay: i * 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  className="askai-followup-chip"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    background: t.color.surface,
                    border: `1px solid ${t.color.border}`,
                    borderRadius: t.radius.pill,
                    fontSize: 13,
                    color: t.color.text,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    transition:
                      "background 0.18s ease, border-color 0.18s ease",
                    textAlign: "left",
                    maxWidth: "100%",
                  }}
                >
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 340,
                    }}
                  >
                    {q}
                  </span>
                  <ArrowRightOutlined
                    style={{ fontSize: 11, color: t.color.accent }}
                  />
                </motion.button>
              ))}
            </div>
          </div>
        )}

      {!message.pending && hasContent && (
        <MessageActions
          message={message}
          copied={copied}
          feedback={feedback}
          showRegenerate={isLastAssistant && canRegenerate}
          onCopy={onCopy}
          onFeedback={onFeedback}
          onShare={onShare}
          onRegenerate={onRegenerate}
          onDownloadImage={onDownloadImage}
        />
      )}
    </motion.div>
  );
};

export default AssistantMessage;
